import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  EnsalamentoRequestDTO,
  EnsalamentoResponseDTO,
  CidadeEstatisticaEnsalamentoDTO,
  CargoEstatisticaEnsalamentoDTO,
  LocalEstatisticaEnsalamentoDTO,
  CandidatoEnsalamentoFiltroDTO,
  FiltrosCandidatos
} from '../models/ensalamento.model';
import { PaginatedResponse } from '../models/api-response.model';

@Injectable({
  providedIn: 'root'
})
export class EnsalamentoService {
  private apiUrl = `${environment.apiUrl}/admin/ensalamento`;

  constructor(private http: HttpClient) {}

  /**
   * Gerar e salvar ensalamento
   * POST /api/admin/ensalamento/gerar
   */
  gerarEnsalamento(request: EnsalamentoRequestDTO): Observable<EnsalamentoResponseDTO> {
    return this.http.post<EnsalamentoResponseDTO>(`${this.apiUrl}/gerar`, request);
  }

  /**
   * Pré-visualizar ensalamento (sem salvar)
   * POST /api/admin/ensalamento/pre-visualizar
   */
  preVisualizarEnsalamento(request: EnsalamentoRequestDTO): Observable<EnsalamentoResponseDTO> {
    return this.http.post<EnsalamentoResponseDTO>(`${this.apiUrl}/pre-visualizar`, request);
  }

  /**
   * Consultar ensalamento do edital
   * GET /api/admin/ensalamento/edital/{editalId}
   */
  consultarEnsalamento(editalId: number): Observable<EnsalamentoResponseDTO> {
    return this.http.get<EnsalamentoResponseDTO>(`${this.apiUrl}/edital/${editalId}`);
  }

  /**
   * Remover ensalamento do edital
   * DELETE /api/admin/ensalamento/edital/{editalId}
   */
  removerEnsalamento(editalId: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/edital/${editalId}`);
  }

  /**
   * Estatísticas de ensalamento por cidade
   * GET /api/admin/ensalamento/edital/{editalId}/estatisticas/cidades
   */
  getEstatisticasPorCidade(editalId: number): Observable<CidadeEstatisticaEnsalamentoDTO[]> {
    return this.http.get<CidadeEstatisticaEnsalamentoDTO[]>(
      `${this.apiUrl}/edital/${editalId}/estatisticas/cidades`
    );
  }

  /**
   * Estatísticas de ensalamento por cargo
   * GET /api/admin/ensalamento/edital/{editalId}/estatisticas/cargos
   */
  getEstatisticasPorCargo(editalId: number): Observable<CargoEstatisticaEnsalamentoDTO[]> {
    return this.http.get<CargoEstatisticaEnsalamentoDTO[]>(
      `${this.apiUrl}/edital/${editalId}/estatisticas/cargos`
    );
  }

  /**
   * Estatísticas de ensalamento por local de prova
   * GET /api/admin/ensalamento/edital/{editalId}/estatisticas/locais
   */
  getEstatisticasPorLocal(editalId: number): Observable<LocalEstatisticaEnsalamentoDTO[]> {
    return this.http.get<LocalEstatisticaEnsalamentoDTO[]>(
      `${this.apiUrl}/edital/${editalId}/estatisticas/locais`
    );
  }

  /**
   * Buscar candidatos com filtros avançados
   * GET /api/admin/ensalamento/edital/{editalId}/candidatos/filtros
   */
  buscarCandidatosFiltrados(
    editalId: number,
    filtros: FiltrosCandidatos,
    page: number = 0,
    size: number = 50
  ): Observable<PaginatedResponse<CandidatoEnsalamentoFiltroDTO>> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString());

    if (filtros.sexo) {
      params = params.set('sexo', filtros.sexo);
    }
    if (filtros.temCondicaoEspecial !== undefined) {
      params = params.set('temCondicaoEspecial', filtros.temCondicaoEspecial.toString());
    }
    if (filtros.numeroInscricao) {
      params = params.set('numeroInscricao', filtros.numeroInscricao);
    }
    if (filtros.nome) {
      params = params.set('nome', filtros.nome);
    }
    if (filtros.estadoId) {
      params = params.set('estadoId', filtros.estadoId.toString());
    }
    if (filtros.cidadeCandidatoId) {
      params = params.set('cidadeCandidatoId', filtros.cidadeCandidatoId.toString());
    }
    if (filtros.apenasPagos !== undefined) {
      params = params.set('apenasPagos', filtros.apenasPagos.toString());
    }
    if (filtros.apenasNaoEnsalados !== undefined) {
      params = params.set('apenasNaoEnsalados', filtros.apenasNaoEnsalados.toString());
    }

    return this.http.get<PaginatedResponse<CandidatoEnsalamentoFiltroDTO>>(
      `${this.apiUrl}/edital/${editalId}/candidatos/filtros`,
      { params }
    );
  }
}
